/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import AccordionWrapper from '../../accordion/Accordion';
import {AccordionSectionHeader} from '../../accordion/current/AccordionSectionHeader';
import {AssetSummaryCardBody} from '../../assetcard/current/AssetSummaryCardBody';
import {ASSET_DETAILS_CARD_WIDTH} from './assetDetailsAccordionStyles';

interface AssetDetailsAccordionProps {
  location?: string;
  group?: string;
  route?: string;
  driver?: string;
  onDriverClick?: () => void;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // ION-7461: every card in the Asset Details Drawer is a fixed 456px ...
  wrapper : {
    boxSizing : 'border-box',
    width     : ASSET_DETAILS_CARD_WIDTH
  },
  // padding now comes from AccordionWrapper's own detailsRoot (current
  // theme default: 8px 16px 24px 16px) rather than being re-applied here ...
  body : {
    boxSizing : 'border-box',
    width     : '100%'
  }
}));

/**
 * AssetDetailsAccordion renders the ION-7461 "Asset Details" accordion -
 * built from the Body Section of the Asset Summary Card (ION-7396), reusing
 * `AssetSummaryCardBody`'s address/group/route/driver rows unchanged
 * (the Driver row renders as plain text, clickable as a whole row via
 * `onDriverClick` - not a hyperlink), at this drawer's fixed 456px card
 * width. Per the Figma spec, the accordion header has no info icon and no
 * numeric count badge - just the uppercase title.
 *
 * Expansion is uncontrolled via `defaultExpanded` by default; passing
 * `expanded` and `onExpand` instead hands control to the caller, which the
 * Asset Details Drawer uses to drive its header's expand/collapse-all
 * control. Both are forwarded straight to `AccordionWrapper`.
 *
 * ```typescript
 *
 *   import {AssetDetailsAccordion} from
 *     '@ion/dls/src/components/assetdetails/current/AssetDetailsAccordion';
 *   ...
 *
 *   <AssetDetailsAccordion location='13505 Zachary Taylor Dr, Manassas, Virginia'
 *     group='STREETS & STREETS ROADSIDE VEGETATION'
 *     route='Route Name'
 *     driver='Thomas Thumb'
 *     onDriverClick={onDriverClick} />
 *
 * ```
 *
 */
export const AssetDetailsAccordion = memo(({
  location,
  group,
  route,
  driver,
  onDriverClick,
  defaultExpanded,
  expanded,
  onExpand
}: AssetDetailsAccordionProps) => {
  const classes = useStyles();
  return (
    <div className={classes.wrapper}>
      <AccordionWrapper isCustomStyle
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onExpand={onExpand}
        header={<AccordionSectionHeader title='ASSET DETAILS' />}
        details={
          <div className={classes.body}>
            <AssetSummaryCardBody location={location}
              group={group}
              route={route}
              driver={driver}
              onDriverClick={onDriverClick} />
          </div>
        } />
    </div>
  );
});

export default AssetDetailsAccordion;
